import { useRequest } from 'ahooks';
import type { Options } from 'ahooks/lib/useRequest/src/types';
import { taxTypeApi } from '@/services/api';
import { useCountriesData } from '@/hooks/useLocations';
import { trackCacheKey } from '@/utils/cacheKeyRegistry';
import type { TaxTypesResponse } from '@/types/api/taxType';

/**
 * Hook to fetch tax types for a specific country
 *
 * Callers hold a numeric countryId, but the /api/tax-types endpoint
 * requires the ISO countryCode (drift D-REF-1) — the id is mapped to
 * its code here via the cached countries list so all consumers stay
 * unchanged.
 *
 * @param countryId - Country ID (integer)
 * @param config - ahooks useRequest configuration options
 */
export function useTaxTypes(countryId?: number, config?: Options<TaxTypesResponse, []>) {
  const { countries, isLoading: countriesLoading } = useCountriesData();
  const countryCode = countryId
    ? countries.find((country) => country.countryId === countryId)?.countryCode
    : undefined;

  const { data, error, loading, refresh } = useRequest(
    () => {
      if (!countryCode) {
        // Return empty response until a country is selected and resolved
        return Promise.resolve({ data: [] });
      }
      return taxTypeApi.getByCountry(countryCode);
    },
    {
      refreshDeps: [countryCode],
      cacheKey: countryCode
        ? trackCacheKey(`/api/tax-types?countryCode=${countryCode}`)
        : undefined,
      staleTime: 5 * 60 * 1000, // 5 minutes - tax types don't change often
      ...config,
    }
  );

  return {
    taxTypes: data?.data ?? [],
    error,
    // Also loading while the id→code resolution waits on the countries
    // list — otherwise a cold cache flashes "no options" for a valid country
    isLoading: loading || (!!countryId && !countryCode && countriesLoading),
    mutate: refresh,
    refresh,
  };
}
